/**
 * Listado de proveedores. El merch propio no es un distribuidor: enlaza al stock.
 */

import { useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ResourceIndex, type ResourceIndexColumn } from '@/components/shared/ResourceIndex';
import {
    parseResourceIndexQuery,
    selectPageIds,
    toggleSelectedId,
    writeResourceIndexQuery,
} from '@/components/shared/resourceIndexState';
import { StatusBadge } from '@/components/shared/StatusBadge';
import { esCount } from '@/lib/es-count';
import { formatDate, formatEurFromCents, formatNumber } from '@/lib/format-es';
import { useCatalogSuppliers } from '../catalog/hooks/useStoreCatalog';
import type { CatalogSupplierRow } from '../catalog/types';
import { supplierFreshness } from './supplierFreshness';
import { TEMPLATE_HELP_PATH } from './templateManual';
import { useMerchSummary } from './useMerchSummary';

const VIEWS = ['all', 'sync_fresh', 'sync_stale', 'unsynced', 'inactive'] as const;
const VIEW_LABELS: Record<(typeof VIEWS)[number], string> = {
    all: 'Todos',
    sync_fresh: 'Al día',
    sync_stale: 'Sync caducada',
    unsynced: 'Sin sincronizar',
    inactive: 'Inactivos',
};
const INDEX_URL = { views: VIEWS, defaultView: 'all' as const, filterKeys: [] as const };
const PAGE_SIZE = 50;

interface SupplierListRow {
    id: string;
    kind: 'merch' | 'supplier';
    name: string;
    slug: string;
    logoPath: string | null;
    status: string;
    lastOk: string | null;
    activeFichas: number;
    salesCents: number;
    orderCount: number;
    hasEmail: boolean;
}

function initials(name: string): string {
    const parts = name.split(/\s+/).filter(Boolean);
    return (parts[0]?.[0] ?? '').concat(parts[1]?.[0] ?? '').toUpperCase();
}

function supplierRow(supplier: CatalogSupplierRow): SupplierListRow {
    return {
        id: supplier.slug,
        kind: 'supplier',
        name: supplier.name,
        slug: supplier.slug,
        logoPath: supplier.logoPath ?? null,
        status: supplierFreshness({
            slug: supplier.slug,
            isActive: supplier.isActive,
            lastOkFinishedAt: supplier.lastOk?.finishedAt,
        }),
        lastOk: supplier.lastOk?.finishedAt ?? null,
        activeFichas: supplier.productCount,
        salesCents: supplier.salesCents ?? 0,
        orderCount: supplier.orderCount ?? 0,
        hasEmail: Boolean(supplier.orderContactEmail?.trim()),
    };
}

export function SupplierIndexPage() {
    const navigate = useNavigate();
    const [params, setParams] = useSearchParams();
    const query = useMemo(() => parseResourceIndexQuery(params, INDEX_URL), [params]);
    const [selectedIds, setSelectedIds] = useState<string[]>([]);
    const suppliersQ = useCatalogSuppliers();
    const merchQ = useMerchSummary();

    const suppliers = useMemo(
        () => (suppliersQ.data ?? []).map((supplier) => supplierRow(supplier)),
        [suppliersQ.data],
    );
    const counts = useMemo(() => {
        const next: Record<string, number> = { all: suppliers.length };
        for (const view of VIEWS) {
            if (view === 'all') continue;
            next[view] = suppliers.filter((row) => row.status === view).length;
        }
        return next;
    }, [suppliers]);

    const merch = merchQ.data;
    const merchRow: SupplierListRow | null = merch
        ? {
            id: 'merch',
            kind: 'merch',
            name: 'AiPeces',
            slug: 'merch',
            logoPath: '/aipeces-mark-dark-ui.png',
            status: 'active',
            lastOk: null,
            activeFichas: merch.productActive,
            salesCents: merch.salesCents,
            orderCount: merch.orderCount,
            hasEmail: false,
        }
        : null;

    const needle = query.q.trim().toLowerCase();
    const visibleSuppliers = suppliers.filter((row) => {
        if (query.view !== 'all' && row.status !== query.view) return false;
        if (!needle) return true;
        return `${row.name} ${row.slug}`.toLowerCase().includes(needle);
    });
    const ordered = [...visibleSuppliers].sort((a, b) => a.name.localeCompare(b.name, 'es'));
    const merchMatches = !needle || 'aipeces merch stock propio'.includes(needle);
    const rows = query.view === 'all' && merchRow && merchMatches ? [merchRow, ...ordered] : ordered;
    const pageRows = rows.slice((query.page - 1) * PAGE_SIZE, query.page * PAGE_SIZE);

    const columns: ResourceIndexColumn<SupplierListRow>[] = [
        {
            id: 'name',
            header: 'Proveedor',
            cell: (row) => (
                <span className="flex items-center gap-3">
                    <span className="flex h-10 w-16 shrink-0 items-center justify-center overflow-hidden rounded-md border border-border bg-white">
                        {row.logoPath ? (
                            <img src={row.logoPath} alt="" className="max-h-8 max-w-14 object-contain" />
                        ) : (
                            <span className="text-xs font-semibold text-slate-500">{initials(row.name)}</span>
                        )}
                    </span>
                    <span>
                        <span className="block font-medium text-foreground">{row.name}</span>
                        <span className="block text-xs text-muted-foreground">
                            {row.kind === 'merch' ? 'Stock propio' : row.slug}
                        </span>
                    </span>
                </span>
            ),
        },
        {
            id: 'status',
            header: 'Estado',
            cell: (row) => (
                row.kind === 'merch'
                    ? <StatusBadge status="active" label="Stock propio" />
                    : <StatusBadge status={row.status} />
            ),
        },
        {
            id: 'lastOk',
            header: 'Última sync correcta',
            cell: (row) => (
                <span className="tabular-nums text-muted-foreground">
                    {row.kind === 'merch' ? '—' : formatDate(row.lastOk)}
                </span>
            ),
        },
        {
            id: 'fichas',
            header: 'Fichas activas',
            align: 'right',
            cell: (row) => <span className="tabular-nums">{formatNumber(row.activeFichas)}</span>,
        },
        {
            id: 'sales',
            header: 'Ventas',
            align: 'right',
            cell: (row) => (
                <span className="tabular-nums">
                    {formatEurFromCents(row.salesCents)}
                    <span className="block text-xs text-muted-foreground">
                        {esCount(row.orderCount, 'pedido')}
                    </span>
                </span>
            ),
        },
        {
            id: 'email',
            header: 'Email de pedidos',
            cell: (row) => (row.kind === 'merch' ? '—' : row.hasEmail ? 'Sí' : 'No'),
        },
    ];

    function commit(next: typeof query) {
        const written = writeResourceIndexQuery(params, next, INDEX_URL);
        if (written.toString() !== params.toString()) setParams(written);
    }

    return (
        <div className="space-y-4">
            <div className="flex justify-end gap-4">
                <Link className="text-sm font-medium text-primary hover:underline" to={TEMPLATE_HELP_PATH}>
                    Instrucciones
                </Link>
                <a
                    className="text-sm font-medium text-primary hover:underline"
                    href="/store-suppliers/plantilla-proveedor-aipeces.csv"
                    download
                >
                    Descargar plantilla CSV
                </a>
            </div>
            <ResourceIndex
                views={VIEWS.map((id) => ({ id, label: VIEW_LABELS[id], count: counts[id] }))}
                view={query.view}
                onViewChange={(id) => {
                    setSelectedIds([]);
                    commit({ ...query, view: id, page: 1 });
                }}
                search={query.q}
                onSearchChange={(value) => commit({ ...query, q: value, page: 1 })}
                searchPlaceholder="Buscar proveedor…"
                chips={[]}
                onRemoveChip={() => undefined}
                columns={columns}
                rows={pageRows}
                rowId={(row) => row.id}
                sort={null}
                sortDir="asc"
                onSort={() => undefined}
                selectedIds={selectedIds}
                onToggleRow={(id) => setSelectedIds((prev) => toggleSelectedId(prev, id))}
                onTogglePage={(checked) => setSelectedIds((prev) => selectPageIds(prev, pageRows.map((row) => row.id), checked))}
                page={query.page}
                pageSize={PAGE_SIZE}
                total={rows.length}
                onPageChange={(page) => commit({ ...query, page })}
                isLoading={suppliersQ.isLoading}
                error={suppliersQ.isError ? 'No se ha podido cargar el listado de proveedores.' : null}
                emptyTitle="No hay proveedores en esta vista"
                onRowClick={(row) => {
                    if (row.kind === 'merch') navigate('/store/products/stock');
                    else navigate(`/store/suppliers/${row.slug}`);
                }}
            />
        </div>
    );
}
